// IMG-6 crop: drag a rectangle over an image to trim it to that region. Non-destructive
// (stored as normalized crop + adjusted placed box); Esc cancels.
import { type Vec } from "../core/geometry";
import type { ImageEntity } from "../doc/entities";
import { cropPatch, pointInImage } from "../doc/imageGeom";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class CropTool implements Tool {
  readonly id = "crop" as const;
  private targetId: string | null = null;
  private start: Vec | null = null;
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  setTarget(id: string): void {
    this.targetId = id;
  }

  private entity(): ImageEntity | null {
    const e = this.targetId ? this.ctx.store.doc.entities[this.targetId] : undefined;
    return e && e.type === "image" ? e : null;
  }

  activate(): void {
    this.start = null;
    this.cursor = null;
    if (!this.entity()) {
      // dead target: reset first so no input lands before the deferred switch
      queueMicrotask(() => this.ctx.switchTool("select"));
      return;
    }
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Crop: drag a rectangle over the part of the image to keep · Esc = cancel");
    this.ctx.selection.set([this.targetId!]);
    this.ctx.requestRender();
  }

  deactivate(): void {
    this.start = null;
  }

  cancel(): void {
    this.start = null;
  }

  inProgress(): boolean {
    return this.start !== null;
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    const entity = this.entity();
    if (!entity) return;
    // start only if the press is on the image (avoids accidental empty crops)
    if (!pointInImage(entity, e.world)) return;
    this.start = e.world;
    this.ctx.gestureActive(true);
    this.ctx.setCursorTip("release to crop");
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.world;
    if (this.start) this.ctx.requestRender();
  }

  pointerUp(e: ToolPointerEvent): void {
    if (!this.start) return;
    const entity = this.entity();
    this.ctx.gestureActive(false);
    if (entity) {
      const patch = cropPatch(entity, this.start, e.world);
      if (patch) {
        this.ctx.store.dispatch({
          kind: "updateEntities",
          patches: [
            {
              id: entity.id,
              before: { pos: entity.pos, wMm: entity.wMm, hMm: entity.hMm, crop: entity.crop },
              after: patch
            }
          ]
        });
      }
    }
    this.start = null;
    this.ctx.switchTool("select");
  }

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape") {
      this.start = null;
      this.ctx.switchTool("select");
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  renderDynamic(v: RenderView): void {
    if (!this.start || !this.cursor) return;
    const { ctx } = v;
    const px = (n: number): number => n / v.cam.scale;
    ctx.strokeStyle = "#4dd0e1";
    ctx.lineWidth = px(1.5);
    ctx.setLineDash([px(5), px(4)]);
    const x = Math.min(this.start.x, this.cursor.x);
    const y = Math.min(this.start.y, this.cursor.y);
    ctx.strokeRect(x, y, Math.abs(this.cursor.x - this.start.x), Math.abs(this.cursor.y - this.start.y));
    ctx.setLineDash([]);
  }
}
